'use client';

import { useState, useEffect } from 'react';
import { getProductos, getPlanes } from '../../services/divinaPlanesService';
import { toast } from 'sonner';
// Conexión desde las env (REACT_APP_SUPABASE_URL/KEY), como el resto de tadmin.
// Antes estaban hardcodeadas contra divina producción: la pantalla escribía en
// datos reales aunque se entrara por tadmin.dev, y la llave viajaba en el bundle.
import { supabaseUrl as SB_URL, supabaseKey as SB_KEY } from '../../services/supabase/supabaseClient';

// ── Yool brand tokens ──────────────────────────────────────────────
const C = {
  cta:     '#007D6E',
  ctaHov:  '#006558',
  black:   '#1A1A1A',
  navy:    '#0E2640',
  gray1:   '#54585A',
  muted:   '#8D8D8D',
  border:  '#E0E0E0',
  grayBg:  '#EBEBEB',
  page:    '#F7F7F7',
  success: '#158B5C',
  error:   '#ED4068',
  white:   '#FFFFFF',
};

// ── Icono SVG inline (stroke, sin lucide) ─────────────────────────
function Icon({ d, size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  );
}

// ── Badge de paso ─────────────────────────────────────────────────
function StepBadge({ n, done, active }) {
  const bg   = done ? C.success : active ? C.cta : C.grayBg;
  const text = done ? C.white   : active ? C.white : C.muted;
  return (
    <div style={{ width: 24, height: 24, borderRadius: '50%', background: bg,
      color: text, fontSize: 11, fontWeight: 600, display: 'flex',
      alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {done ? '✓' : n}
    </div>
  );
}

// ── Pill seleccionable ─────────────────────────────────────────────
function Pill({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      border: `1.5px solid ${active ? C.cta : C.border}`,
      borderRadius: 100, padding: '5px 14px', fontSize: 12, fontWeight: 500,
      color: active ? C.cta : C.gray1, background: active ? '#E6F5F3' : C.white,
      cursor: 'pointer', transition: 'all .15s', fontFamily: 'inherit',
    }}>{label}</button>
  );
}

// ── Select nativo estilizado ───────────────────────────────────────
function YSelect({ id, value, onChange, children, disabled }) {
  return (
    <select id={id} value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
      style={{ border: `1.5px solid ${C.border}`, borderRadius: 8, padding: '8px 12px',
        fontSize: 14, fontFamily: 'inherit', color: C.black, background: C.white,
        outline: 'none', width: '100%', opacity: disabled ? .5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
      {children}
    </select>
  );
}

// ── Etiqueta de campo ──────────────────────────────────────────────
function FieldLabel({ children }) {
  return (
    <label style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.08em',
      textTransform: 'uppercase', color: C.gray1, display: 'block', marginBottom: 6 }}>
      {children}
    </label>
  );
}

// ── Tarjeta de paso ───────────────────────────────────────────────
function StepCard({ step, done, active, title, sub, children }) {
  const borderColor = done ? '#9FE1CB' : active ? C.cta : C.border;
  return (
    <div style={{ border: `1.5px solid ${borderColor}`, borderRadius: 12,
      padding: '1.25rem', marginBottom: 12, background: C.white, transition: 'border-color .2s' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: children ? 14 : 0 }}>
        <StepBadge n={step} done={done} active={active} />
        <span style={{ fontSize: 14, fontWeight: 500, color: C.black }}>{title}</span>
        <span style={{ fontSize: 12, color: C.muted, marginLeft: 'auto' }}>{sub}</span>
      </div>
      {children}
    </div>
  );
}

// ── Tarjeta de producto ───────────────────────────────────────────
function ProductCard({ prod, selected, onClick }) {
  return (
    <div onClick={onClick} style={{
      border: `1.5px solid ${selected ? C.cta : C.border}`,
      borderRadius: 10, padding: '12px 14px', cursor: 'pointer',
      background: selected ? '#E6F5F3' : C.white,
      display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8,
      transition: 'all .15s',
    }}>
      <span style={{ background: C.grayBg, borderRadius: 6, padding: '3px 8px',
        fontSize: 11, fontWeight: 500, color: C.gray1, flexShrink: 0 }}>
        {prod.ramo || 'salud'}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: selected ? C.cta : C.black }}>
          {prod.yoolbrand || prod.nombre}
        </div>
        <div style={{ fontSize: 12, color: C.muted }}>{prod.compania || 'Compañía'}</div>
      </div>
      <span style={{ fontSize: 12, color: C.success, marginLeft: 'auto', fontWeight: 500, flexShrink: 0 }}>
        {prod.planesCount} plan{prod.planesCount !== 1 ? 'es' : ''}
      </span>
    </div>
  );
}

// ── Tarjeta de plan ───────────────────────────────────────────────
function PlanCard({ plan, selected, onClick, precioMin }) {
  return (
    <div onClick={onClick} style={{
      border: `1.5px solid ${selected ? C.cta : C.border}`,
      borderRadius: 10, padding: 12, cursor: 'pointer',
      background: selected ? '#E6F5F3' : C.white, transition: 'all .15s',
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: selected ? C.cta : C.black, marginBottom: 4 }}>
        {plan.nombre}
      </div>
      <div style={{ fontSize: 12, color: C.gray1 }}>
        {plan.cobertura} cobertura · UF {plan.capital_uf}
      </div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
        {precioMin !== null
          ? <><span style={{ color: C.cta, fontWeight: 600 }}>Desde UF {precioMin.toFixed(2)}</span>/mes</>
          : 'Sin precios cargados'}
      </div>
    </div>
  );
}

// ── Tarjeta de resultado ──────────────────────────────────────────
function ResultCard({ label, content, type, canal, planNombre }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    toast.success('Copiado al portapapeles');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`,
      borderRadius: 12, padding: '1.25rem', position: 'relative', marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.1em',
        textTransform: 'uppercase', color: C.muted, marginBottom: 10 }}>
        {label}
      </div>

      {type === 'hook' && (
        <>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.black, lineHeight: 1.3 }}>
            {content}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
            {[`${content.split(/\s+/).length} palabras`, canal, planNombre].map(t => (
              <span key={t} style={{ fontSize: 12, color: C.muted,
                background: C.page, padding: '3px 10px', borderRadius: 100 }}>{t}</span>
            ))}
          </div>
        </>
      )}

      {type === 'cta' && (
        <div style={{ display: 'inline-block', background: C.cta, color: C.white,
          fontSize: 14, fontWeight: 600, padding: '8px 18px', borderRadius: 8, marginTop: 4 }}>
          {content}
        </div>
      )}

      {(type === 'idea' || type === 'caption' || type === 'variantes') && (
        <div style={{ fontSize: 15, lineHeight: 1.65, color: C.black, whiteSpace: 'pre-wrap' }}>
          {content}
        </div>
      )}

      <button onClick={copy} style={{
        position: 'absolute', top: 12, right: 12,
        border: `1.5px solid ${copied ? C.success : C.border}`,
        borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 500,
        color: copied ? C.success : C.gray1, background: C.white,
        cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s',
      }}>
        {copied ? 'Copiado' : 'Copiar'}
      </button>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════
export default function ContentAgent() {
  // Data
  const [productos, setProductos]     = useState([]);
  const [planes, setPlanes]           = useState([]);
  const [precios, setPrecios]         = useState({});   // planId → [{tramo_etario, precio_uf}]
  const [loadingDB, setLoadingDB]     = useState(true);

  // Selección
  const [selProducto, setSelProducto] = useState(null);
  const [selPlan, setSelPlan]         = useState(null); // null = todos

  // Parámetros
  const [canal, setCanal]             = useState('ig_reels');
  const [publico, setPublico]         = useState('fonasa_dep');
  const [objetivo, setObjetivo]       = useState('atraer');
  const [tono, setTono]               = useState('directo');
  const [formatos, setFormatos]       = useState(['hook', 'idea', 'cta']);

  // Generación
  const [loading, setLoading]         = useState(false);
  const [resultados, setResultados]   = useState([]);

  const CANAL_LABELS   = { ig_reels:'Instagram Reels', ig_feed:'Instagram Feed', facebook:'Facebook', whatsapp:'WhatsApp', tiktok:'TikTok', email:'Email', landing:'Landing page', google_ads:'Google Ads' };
  const PUBLICO_LABELS = { fonasa_dep:'FONASA trabajador dependiente', fonasa_ind:'FONASA independiente', ex_isapre:'Ex ISAPRE (migró a FONASA)', isapre_caro:'ISAPRE con precio que sube', sin_comp:'Sin seguro complementario', familia:'Familia con hijos', adulto_mayor:'Adulto mayor 55+' };
  const OBJETIVO_LABELS= { atraer:'Atraer — awareness', filtrar:'Filtrar leads', cerrar:'Cerrar — llevar a cotizar', retargeting:'Retargeting', educar:'Educar sobre el producto' };
  const TONO_LABELS    = { directo:'Directo con datos', empatico:'Empático y cercano', urgente:'Urgencia real', educativo:'Educativo', comparativo:'Comparativo (vs FONASA/ISAPRE)' };
  const FMT_LABELS     = { hook:'Hook', idea:'Idea de contenido', cta:'CTA', caption:'Caption completo', variantes:'3 variantes de hook' };

  // ── Carga inicial ──────────────────────────────────────────────
  useEffect(() => { loadDB(); }, []);

  async function loadDB() {
    try {
      setLoadingDB(true);
      const [prods, planesData] = await Promise.all([getProductos(), getPlanes()]);
      
      // Agrupar planes por producto
      const planesPorProd = {};
      (planesData || []).forEach(pl => {
        const pid = pl.producto_id || pl.producto?.id;
        if (!planesPorProd[pid]) planesPorProd[pid] = [];
        planesPorProd[pid].push(pl);
      });

      const prodsActivos = (prods || []).map(p => ({
        ...p,
        planesCount: (planesPorProd[p.id] || []).length,
      }));
      setProductos(prodsActivos);

      // Fetch precios desde Supabase directamente (REST)
      const planIds = (planesData || []).map(p => p.id);
      if (planIds.length) {
        const r = await fetch(`${SB_URL}/rest/v1/precios_base?select=plan_id,tramo_etario,precio_uf&plan_id=in.(${planIds.join(',')})`, {
          headers: { apikey: SB_KEY, Authorization: `Bearer ${SB_KEY}` }
        });
        if (r.ok) {
          const rows = await r.json();
          const map = {};
          rows.forEach(row => {
            if (!map[row.plan_id]) map[row.plan_id] = [];
            map[row.plan_id].push(row);
          });
          setPrecios(map);
        }
      }

      setPlanes(planesData || []);
    } catch (e) {
      toast.error('Error al cargar datos de Supabase');
    } finally {
      setLoadingDB(false);
    }
  }

  // ── Planes del producto seleccionado ──────────────────────────
  const planesDelProducto = selProducto
    ? planes.filter(pl => (pl.producto_id || pl.producto?.id) === selProducto.id)
    : [];

  function precioMin(planId) {
    const rows = precios[planId] || [];
    if (!rows.length) return null;
    return Math.min(...rows.map(r => parseFloat(r.precio_uf)));
  }

  // ── Construcción del contexto para la API ─────────────────────
  function buildContext() {
    if (!selProducto) return '';
    const p = selProducto;
    const lines = [
      `Producto: ${p.yoolbrand || p.nombre}`,
      `Ramo: ${p.ramo || 'salud'}`,
    ];
    if (p.descripcion_json?.titulo)    lines.push(`Marca: ${p.descripcion_json.titulo}`);
    if (p.descripcion_json?.subtitulo) lines.push(`Descripción: ${p.descripcion_json.subtitulo}`);
    if (p.descripcion_json?.card)      p.descripcion_json.card.forEach(c => lines.push(`${c.titulo}: ${c.content}`));

    const targetPlanes = selPlan ? [selPlan] : planesDelProducto;

    if (!selPlan && planesDelProducto.length > 1) {
      lines.push('\nPLANES DISPONIBLES:');
    } else if (selPlan) {
      lines.push('\nPLAN SELECCIONADO:');
    }

    targetPlanes.forEach(pl => {
      const min = precioMin(pl.id);
      lines.push(`- ${pl.nombre}: cobertura ${pl.cobertura}, capital UF ${pl.capital_uf}${min !== null ? `, desde UF ${min.toFixed(2)}/mes` : ''}`);
      const ja = typeof pl.jsonarray === 'string' ? tryParse(pl.jsonarray) : pl.jsonarray;
      if (ja?.items) {
        ja.items.filter(i => i.tipo === 'beneficio').slice(0, 4).forEach(b => lines.push(`  Beneficio: ${b.item}`));
        if (ja.comunicacion?.titulo) lines.push(`  Tagline: ${ja.comunicacion.titulo}`);
      }
      const rowsPrecios = precios[pl.id] || [];
      if (rowsPrecios.length) {
        lines.push(`  Precios por tramo: ${rowsPrecios.map(r => `${r.tramo_etario}: UF ${parseFloat(r.precio_uf).toFixed(2)}`).join(' | ')}`);
      }
    });

    return lines.join('\n');
  }

  // ── Generación via API Claude ─────────────────────────────────
  async function generate() {
    if (!selProducto) { toast.error('Selecciona un producto primero'); return; }
    if (!formatos.length) { toast.error('Selecciona al menos un formato'); return; }

    setLoading(true);
    setResultados([]);

    const FMT_INSTRUCTIONS = {
      hook:      'HOOK: Máximo 12 palabras. Basado en dolor o beneficio concreto del producto.',
      idea:      'IDEA: 2-3 oraciones describiendo el concepto de contenido a producir.',
      cta:       'CTA: Llamado a la acción específico para este producto, máximo 10 palabras.',
      caption:   'CAPTION: Caption completo listo para publicar, máximo 150 palabras.',
      variantes: 'VARIANTES:\n1. [hook 1]\n2. [hook 2]\n3. [hook 3]',
    };

    const systemPrompt = `Eres el Content Conversion Agent de Yool — broker digital de seguros de salud en Chile. Tu tarea es crear contenido de conversión que genere leads calificados.

REGLAS ABSOLUTAS:
- Usar SOLO los datos del producto/plan provistos desde Supabase. No inventar coberturas, precios ni beneficios.
- Si hay precios en UF o capital anual, úsalos en el copy con números reales.
- Lenguaje simple, directo, segunda persona (tú). Sin jerga técnica sola.
- Prohibido: "protege tu salud", "tu bienestar nos importa", "la mejor opción", frases vacías.
- Contexto chileno siempre: FONASA, ISAPRE, costos reales de salud.
- El hook debe resonar como un dato que duele o sorprende — no como publicidad.
- El CTA es específico al producto, nunca genérico.
- RESPONDE SOLO con los formatos solicitados. Sin introducción, sin explicación previa.`;

    const userPrompt = `DATOS DEL PRODUCTO (fuente: Supabase proyecto divina):
${buildContext()}

PARÁMETROS:
- Canal: ${CANAL_LABELS[canal]}
- Público: ${PUBLICO_LABELS[publico]}
- Objetivo: ${OBJETIVO_LABELS[objetivo]}
- Tono: ${TONO_LABELS[tono]}

FORMATOS SOLICITADOS:
${formatos.map(f => FMT_INSTRUCTIONS[f]).join('\n')}`;

    try {
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1000,
          system: systemPrompt,
          messages: [{ role: 'user', content: userPrompt }],
        }),
      });
      const data = await res.json();
      const text = data.content?.find(b => b.type === 'text')?.text || '';
      setResultados(parseOutput(text, formatos));
      toast.success('Contenido generado');
    } catch (e) {
      toast.error('Error al conectar con la API');
    } finally {
      setLoading(false);
    }
  }

  // ── Parse output ──────────────────────────────────────────────
  function parseOutput(text, fmts) {
    const results = [];
    fmts.forEach(f => {
      const re = new RegExp(`(?:${f.toUpperCase()}|${f[0].toUpperCase() + f.slice(1)}):\\s*([\\s\\S]*?)(?=\\n(?:HOOK|IDEA|CTA|CAPTION|VARIANTES):|$)`, 'i');
      const m = text.match(re);
      if (m) results.push({ type: f, label: FMT_LABELS[f], content: m[1].trim() });
    });
    if (!results.length) results.push({ type: 'idea', label: 'Resultado', content: text.trim() });
    return results;
  }

  function tryParse(v) { try { return typeof v === 'string' ? JSON.parse(v) : v; } catch { return null; } }

  const canalLabel  = CANAL_LABELS[canal];
  const planLabel   = selPlan ? selPlan.nombre : (selProducto?.yoolbrand || selProducto?.nombre || '—');

  // ── Render ────────────────────────────────────────────────────
  return (
    <div style={{ background: C.page, minHeight: '100vh', padding: '2rem 1rem', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif' }}>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1.75rem' }}>

          <span style={{ background: '#E1F5EE', color: '#0F6E56', fontSize: 11, fontWeight: 600,
            letterSpacing: '.1em', textTransform: 'uppercase', padding: '4px 10px', borderRadius: 100 }}>
            Agent 1
          </span>
          <span style={{ fontSize: 11, color: C.muted, background: C.grayBg, padding: '4px 10px', borderRadius: 100 }}>
            v3 · live DB
          </span>
          <span style={{ fontSize: 13, color: C.gray1, marginLeft: 'auto' }}>Content Conversion Agent</span>
        </div>

        {loadingDB ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: C.muted, fontSize: 14, padding: '2rem 0' }}>
            <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${C.border}`,
              borderTopColor: C.cta, animation: 'spin .7s linear infinite' }} />
            Cargando productos y planes desde Supabase...
          </div>
        ) : (
          <>
            {/* STEP 1 — Producto */}
            <StepCard step={1} done={!!selProducto} active={!selProducto}
              title="Producto"
              sub={selProducto ? `${productos.length} producto${productos.length !== 1 ? 's' : ''} activo${productos.length !== 1 ? 's' : ''}` : 'Cargado desde Supabase'}>
              {productos.map(p => (
                <ProductCard key={p.id} prod={p}
                  selected={selProducto?.id === p.id}
                  onClick={() => { setSelProducto(p); setSelPlan(null); setResultados([]); }} />
              ))}
            </StepCard>

            {/* STEP 2 — Plan */}
            <StepCard step={2} done={selProducto !== null} active={selProducto !== null}
              title="Plan"
              sub={selProducto
                ? `${planesDelProducto.length} plan${planesDelProducto.length !== 1 ? 'es' : ''} disponible${planesDelProducto.length !== 1 ? 's' : ''}`
                : 'Selecciona un producto primero'}>
              {selProducto && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                  {/* Opción: todos */}
                  <PlanCard
                    plan={{ nombre: 'Todos los planes', cobertura: '—', capital_uf: '—' }}
                    selected={selPlan === null && selProducto !== null}
                    onClick={() => setSelPlan(null)}
                    precioMin={null} />
                  {planesDelProducto.map(pl => (
                    <PlanCard key={pl.id} plan={pl}
                      selected={selPlan?.id === pl.id}
                      onClick={() => { setSelPlan(pl); setResultados([]); }}
                      precioMin={precioMin(pl.id)} />
                  ))}
                </div>
              )}
              {!selProducto && (
                <span style={{ fontSize: 12, color: C.muted, fontStyle: 'italic' }}>
                  Los planes aparecerán aquí
                </span>
              )}
            </StepCard>

            {/* STEP 3 — Parámetros */}
            <StepCard step={3} done={false} active={!!selProducto}
              title="Parámetros de contenido" sub="Canal, público y objetivo">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div>
                  <FieldLabel>Canal</FieldLabel>
                  <YSelect id="canal" value={canal} onChange={setCanal}>
                    {Object.entries(CANAL_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </YSelect>
                </div>
                <div>
                  <FieldLabel>Público objetivo</FieldLabel>
                  <YSelect id="publico" value={publico} onChange={setPublico}>
                    {Object.entries(PUBLICO_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </YSelect>
                </div>
                <div>
                  <FieldLabel>Objetivo del contenido</FieldLabel>
                  <YSelect id="objetivo" value={objetivo} onChange={setObjetivo}>
                    {Object.entries(OBJETIVO_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </YSelect>
                </div>
                <div>
                  <FieldLabel>Tono</FieldLabel>
                  <YSelect id="tono" value={tono} onChange={setTono}>
                    {Object.entries(TONO_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </YSelect>
                </div>
              </div>

              <div style={{ marginBottom: 10 }}>
                <FieldLabel>Formatos de salida</FieldLabel>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {Object.entries(FMT_LABELS).map(([v, l]) => (
                    <Pill key={v} label={l}
                      active={formatos.includes(v)}
                      onClick={() => setFormatos(f => f.includes(v) ? f.filter(x => x !== v) : [...f, v])} />
                  ))}
                </div>
              </div>
            </StepCard>

            {/* Botón generar */}
            <button onClick={generate} disabled={loading || !selProducto}
              style={{ width: '100%', height: 44, background: (!selProducto || loading) ? C.muted : C.cta,
                color: C.white, border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600,
                cursor: (!selProducto || loading) ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                fontFamily: 'inherit', marginTop: 4, transition: 'background .15s' }}>
              {loading ? (
                <>
                  <div style={{ width: 16, height: 16, borderRadius: '50%',
                    border: '2px solid rgba(255,255,255,.3)', borderTopColor: C.white,
                    animation: 'spin .7s linear infinite' }} />
                  Generando...
                </>
              ) : 'Generar contenido'}
            </button>

            {/* Resultados */}
            {resultados.length > 0 && (
              <div style={{ marginTop: '1.5rem' }}>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.1em',
                  textTransform: 'uppercase', color: C.muted, marginBottom: 12 }}>
                  Resultado
                </div>
                {resultados.map((r, i) => (
                  <ResultCard key={i} {...r} canal={canalLabel} planNombre={planLabel} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
